import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import { ArrowRight, Check, Copy, Eye, EyeOff, Refresh } from '../components/Icons';

export default function ApiKeysScreen() {
  const [publicKey, setPublicKey]   = useState('');
  const [secretKey, setSecretKey]   = useState('');
  const [generatedAt, setGeneratedAt] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [loading, setLoading]       = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError]           = useState(null);
  const [copied, setCopied]         = useState(null);

  useEffect(() => {
    api.getApiKeys()
      .then(data => { setPublicKey(data.publicKey || ''); setSecretKey(data.secretKey || ''); setGeneratedAt(data.keysGeneratedAt || ''); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const copy = (value, label) => {
    navigator.clipboard.writeText(value);
    setCopied(label);
    setTimeout(() => setCopied(null), 1500);
  };

  const generate = async () => {
    const hasKeys = !!publicKey;
    if (hasKeys && !window.confirm('Regenerate API keys? Your existing keys will stop working immediately.')) return;
    setGenerating(true); setError(null);
    try {
      const data = await api.generateApiKeys();
      setPublicKey(data.publicKey || '');
      setSecretKey(data.secretKey || '');
      setGeneratedAt(data.keysGeneratedAt || new Date().toISOString());
      setShowSecret(true);
    } catch (e) {
      setError(e.message || 'Failed to generate keys.');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) return <p className="vt-muted">Loading…</p>;

  const mask = (v) => v ? v.slice(0, 8) + '•'.repeat(24) : '';
  const generatedLabel = generatedAt && !Number.isNaN(Date.parse(generatedAt))
    ? `Generated ${new Date(generatedAt).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}`
    : null;

  return (
    <div>
      <div className="vt-pageHead vt-pageHeadRow">
        <div>
          <div className="vt-eyebrow">Developers</div>
          <h1 className="vt-pageTitle">API keys</h1>
          <p className="vt-pageSub">Authenticate your requests to the Veritage API.</p>
        </div>
        {!publicKey && (
          <button onClick={generate} disabled={generating} className="vt-btn vt-btn--primary vt-btn--lg">
            {generating ? 'Generating…' : 'Generate keys'}
            <span className="vt-btnArrow"><ArrowRight size={13} /></span>
          </button>
        )}
      </div>

      {error && <div className="vt-alert vt-alert--error">{error}</div>}

      <section className="vt-panel">
        <div className="vt-panelHead">
          <div>
            <h2 className="vt-panelTitle">Keys</h2>
            <p className="vt-panelSub">{publicKey ? (generatedLabel || 'Active') : 'Generate a key pair to start creating verification sessions.'}</p>
          </div>
          {publicKey && (
            <button onClick={generate} disabled={generating} className="vt-btn vt-btn--ghostPill">
              <Refresh size={15} /> {generating ? 'Regenerating…' : 'Regenerate'}
            </button>
          )}
        </div>
        <div className="vt-list">
          <KeyRow
            title="Public key"
            tag={<span className="vt-tag">Client</span>}
            hint={<>Send as the <code>X-Api-Key</code> header when creating sessions.</>}
            value={publicKey}
            copied={copied === 'public'}
            onCopy={() => copy(publicKey, 'public')}
          />
          <KeyRow
            title="Secret key"
            tag={<span className="vt-tag vt-tag--danger">Server only</span>}
            hint={<>Validates tokens with <code>POST /api/v1/sessions/verify-token</code>. Never expose it in client code.</>}
            value={secretKey}
            display={showSecret ? secretKey : mask(secretKey)}
            copied={copied === 'secret'}
            onCopy={() => copy(secretKey, 'secret')}
            emptyText={publicKey ? 'Only shown when generated' : undefined}
            extra={secretKey ? (
              <button type="button" className="vt-iconBtn" onClick={() => setShowSecret(v => !v)} aria-label={showSecret ? 'Hide secret key' : 'Reveal secret key'}>
                {showSecret ? <EyeOff size={13} /> : <Eye size={13} />}
              </button>
            ) : null}
          />
        </div>
        <p className="vt-panelFoot">
          New to the API? <a href="https://docs.veritage.io" target="_blank" rel="noreferrer">Read the docs</a> or try it in the{' '}
          <a href="https://playground.veritage.io" target="_blank" rel="noreferrer">Identity Playground</a>.
        </p>
      </section>
    </div>
  );
}

function KeyRow({ title, tag, hint, value, display, emptyText = 'Not generated', onCopy, copied, extra }) {
  return (
    <div className="vt-row vt-row--stack" style={{ alignItems: 'flex-start', paddingTop: 16, paddingBottom: 16 }}>
      <div className="vt-rowBody" style={{ flex: '1 1 260px' }}>
        <div className="vt-rowTitle">{title} {tag}</div>
        <div className="vt-rowMeta">{hint}</div>
      </div>
      <div className="vt-rowActions" style={{ flex: '1 1 340px', minWidth: 0 }}>
        <div className={['vt-value', value ? '' : 'vt-value--empty'].filter(Boolean).join(' ')} style={{ flex: 1 }}>
          <span className="vt-valueText">{value ? (display || value) : emptyText}</span>
          {extra}
          {value && (
            <button type="button" className="vt-iconBtn" onClick={onCopy}>
              {copied ? <Check size={13} /> : <Copy size={13} />} {copied ? 'Copied' : 'Copy'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
